import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { formatPrice } from "@/lib/utils";
import { Badge } from "./ui/badge";
import { getMediaOwner } from "@/data/media-owners";
import { useCart } from "@/utils/cartContext";

const getDefaultFeaturedBillboards = (currencyCode: string) => [
  {
    id: 1,
    src: "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1200&h=600",
    title: "Times Square Premium",
    location: "New York, NY",
    mediaOwnerName: "N/A",
    price: `${currencyCode} 15,000.00/month`,
    alt: "Times Square billboard advertising display",
    inventoryType: "digital",
    type: "DIGITAL"
  },
  {
    id: 2,
    src: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1200&h=600",
    title: "Manhattan Digital Hub",
    location: "New York, NY",
    mediaOwnerName: "N/A",
    price: `${currencyCode} 8,500.00/spot`,
    alt: "Manhattan digital billboard display",
    inventoryType: "digital",
    type: "DIGITAL"
  },
  {
    id: 3,
    src: "https://images.unsplash.com/photo-1541558869434-2840d308329a?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1200&h=600",
    title: "Highway Premium East",
    location: "Los Angeles, CA",
    mediaOwnerName: "N/A",
    price: `${currencyCode} 6,200.00/month`,
    alt: "Highway billboard advertising display",
    inventoryType: "classic",
    type: "CLASSIC"
  }
];

interface FeaturedBillboardsProps {
  featuredBillboards?: any[];
  currencyCode?: string;
  onAddToCart?: (billboard: any) => void;
  onRemoveFromCart?: (billboard: any) => void;
  isInCart?: (id: string | number) => boolean;
  handleInventorySelect?: (billboard: any) => void;
  filters?: { category?: string };
  isLoading?: boolean;
  noDataMessage?: string | null;
}

export default function HeroSection({
  featuredBillboards,
  currencyCode,
  onAddToCart,
  onRemoveFromCart,
  isInCart,
  handleInventorySelect,
  filters,
  isLoading = false,
  noDataMessage = null
}: FeaturedBillboardsProps) {
  const base_path = import.meta.env.VITE_APP_BASE_PATH || '';
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();
  const { priceFormat } = useCart();

  useEffect(() => {
    // If isLoading prop is true, keep isPageLoading true regardless of featuredBillboards
    if (isLoading) {
      setIsPageLoading(true);
    } else if (featuredBillboards && featuredBillboards.length > 0) {
      setIsPageLoading(false);
    }
  }, [featuredBillboards, isLoading]);

  // Helper function to format price with currency
  const formatBillboardPrice = (price: any, inventoryType?: string) => {
    // if (!price) return 'Price on request';

    // Handle different price formats that might come from the API
    let numericPrice = 0;
    if (typeof price === 'string') {
      // Remove currency symbols and convert to number
      const cleanPrice = price.replace(/[^0-9.,]/g, '');
      numericPrice = parseFloat(cleanPrice) || 0;
    } else if (typeof price === 'number') {
      numericPrice = price;
    }

    // If price seems to be in cents (very large number), convert to dollars
    // Otherwise assume it's already in dollars and convert to cents for formatPrice function
    const priceInCents = numericPrice > 10000 ? numericPrice : numericPrice * 100;

    return formatPrice(priceInCents, inventoryType, currencyCode);
  };

  // Use passed data or fallback to default
  const billboardsToDisplay = featuredBillboards && featuredBillboards.length > 0
    ? featuredBillboards.map(billboard => {
      if (filters?.category === 'packages') {
        // Map for package data
        return {
          id: billboard.id,
          src: billboard.thumbnail || `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`,
          title: billboard.name || "Featured Package",
          location: billboard.country?.name || "",
          mediaOwnerName: billboard.publisher?.name || 'Multiple Media Owner',
          price: billboard.totalPrice,
          alt: `${billboard.name || 'Featured Package'} package display`,
          inventoryType: 'package',
          type: 'PACKAGE', // for debug
          currencyCode: billboard.currency || currencyCode || 'N/A',
        };
      }
      // Billboard mapping (existing)
      return {
        id: billboard.id,
        src: billboard.fullResImagePath &&
          billboard.fullResImagePath !== 'null' &&
          billboard.fullResImagePath.trim() !== ''
          ? billboard.fullResImagePath
          : `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`,
        title: billboard.title || billboard.name || "Featured Billboard",
        location: billboard.stateName || billboard.inventoryAddress,
        mediaOwnerName: getMediaOwner(billboard.mediaOwnerName)?.name || billboard.mediaOwnerName || 'N/A',
        price:
          billboard?.type === 'DIGITAL'
            ? (priceFormat === 'spot-based'
              ? billboard?.perSpotPrice
              : billboard?.perDayPrice)
            : (billboard?.sellingRate?.monthly?.month1 && billboard?.sellingRate?.monthly?.month1 > 0)
              ? billboard?.sellingRate?.monthly?.month1
              : billboard?.currentPrice,
        alt: `${billboard.title || 'Featured Billboard'} advertising display`,
        inventoryType: billboard?.inventoryType || billboard?.type || 'digital',
        type: billboard?.type || '', // for debug
        currencyCode: billboard?.currencyCode || currencyCode || 'N/A',
      };
    })
    : getDefaultFeaturedBillboards(currencyCode ?? 'N/A');

  useEffect(() => {
    if (!isHovered) {
      const interval = setInterval(() => {
        nextSlide();
      }, 3000); // Change 3000 to your desired interval in ms
      return () => clearInterval(interval);
    }
  }, [currentIndex, billboardsToDisplay.length, isHovered]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % billboardsToDisplay.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + billboardsToDisplay.length) % billboardsToDisplay.length);
  };

  const currentImage = billboardsToDisplay[currentIndex];

  // Handle add to cart functionality
  const handleAddToCart = () => {
    if (onAddToCart && featuredBillboards && featuredBillboards.length > currentIndex) {
      // Pass the original API data to the parent component
      onAddToCart(featuredBillboards[currentIndex]);
    }
  };

  const handleRemoveFromCart = () => {
    if (onRemoveFromCart && featuredBillboards && featuredBillboards.length > currentIndex) {
      // Pass the original API data to the parent component  
      onRemoveFromCart(featuredBillboards[currentIndex]);
    }
  };

  const currentImageInCart = isInCart && featuredBillboards && featuredBillboards.length > currentIndex
    ? isInCart(featuredBillboards[currentIndex].id)
    : false;

  const handleViewDetails = (inventoryItem: any) => {

    // Get the billboard type from the inventory item
    navigate(`/${base_path}/billboard/${currentImage.id}?type=${encodeURIComponent(currentImage.inventoryType)}`, {
      state: {
        currentPrice: inventoryItem.currentPrice
      }
    });
  };

  // Handle loading state or no data message
  if (isLoading) {
    return (
      <section className="relative bg-white">
        <div className="max-w-[95vw] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="relative h-96 rounded-full">
            <div className="rounded-xl text-center flex flex-col items-center justify-center h-full border">
              <div className="animate-spin w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-gray-600">
                {filters?.category === 'packages' ? 'Loading packages...' : 'Loading inventories...'}
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Hide hero section completely when there are no inventories
  if (noDataMessage || !featuredBillboards || featuredBillboards.length === 0) {
    return null;
  }

  return (
    <section className="relative bg-white">
      <div className="max-w-[95vw] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div
          className="relative h-96 rounded-xl overflow-hidden shadow-lg"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {(isLoading || isPageLoading) || billboardsToDisplay.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full">
              {noDataMessage ? (
                <p className="text-lg text-gray-600">{noDataMessage}</p>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mt-1">Loading inventories...</p>
                  <span className="mt-2 inline-block">
                    <span className="block w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></span>
                  </span>
                </>
              )}
            </div>
          ) : (
            <>
              {currentImageInCart && (
                <Badge
                  className="absolute top-3 left-3 z-10 bg-emerald-400 text-white"
                >
                  In Cart
                </Badge>
              )}

              <img
                src={currentImage?.src ? currentImage?.src : `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`}
                alt={currentImage?.alt || "..."}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = `${import.meta.env.VITE_APP_FOLDER_PATH}/nomedia.jpg`;
                }}
              />
              <div className="absolute inset-0 bg-black/40"></div>
              <div className="absolute bottom-8 left-8 text-white">
                <h2 className="text-3xl font-bold mb-2">{currentImage?.title || "Featured Billboard"}</h2>
                <p className="mb-1">{currentImage?.location || "Location N/A"}</p>
                <span className="mb-1">
                  By {filters?.category === 'packages' ? 'Multiple Media Owner' : (!currentImage?.mediaOwnerName || currentImage?.mediaOwnerName.toLowerCase() === 'n/a') ? 'Multiple Media Owner' : currentImage?.mediaOwnerName}
                </span>
                <p className="font-semibold">
                  {(() => {
                    let price = 0;
                    let suffix = '';
                    let displayCurrencyCode = currencyCode || 'N/A';

                    const billboard = featuredBillboards && featuredBillboards[currentIndex];

                    // Handle package data
                    if (filters?.category === 'packages') {
                      price = billboard?.totalPrice || billboard?.price || 0;
                      suffix = ''; // Packages don't have a per-unit suffix
                      displayCurrencyCode = billboard?.currency || currencyCode || 'N/A';
                    }
                    // Handle digital inventory
                    else if (billboard?.type === 'DIGITAL') {
                      if (priceFormat === 'spot-based') {
                        price = billboard?.perSpotPrice;
                        suffix = '/Spot';
                      } else if (priceFormat === 'day-based') {
                        price = billboard?.perDayPrice;
                        suffix = '/Day';
                      }
                      displayCurrencyCode = billboard?.price?.localCurrencies?.code || billboard?.currencyCode || currencyCode || 'N/A';
                    }
                    // Handle classic/non-digital
                    else {
                      if (billboard?.sellingRate?.monthly?.month1 && billboard.sellingRate.monthly.month1 > 0) {
                        price = billboard.sellingRate.monthly.month1;
                        suffix = '/Month';
                      } else {
                        price = billboard?.currentPrice;
                        suffix = '/Month';
                      }
                      displayCurrencyCode = billboard?.price?.localCurrencies?.code || billboard?.currencyCode || currencyCode || 'N/A';
                    }

                    if (!price || price === 0) return 'N/A';

                    return `${displayCurrencyCode} ${price.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}${suffix}`;
                  })()}
                </p>
                <div className="mt-4 flex space-x-4">
                  <Button variant="secondary"
                    onClick={() => handleInventorySelect && handleInventorySelect(featuredBillboards && featuredBillboards[currentIndex])}
                    className="w-36 h-10 bg-white text-gray-900 hover:bg-gray-100">
                    View Details
                  </Button>
                  <Button
                    onClick={currentImageInCart ? handleRemoveFromCart : handleAddToCart}
                    size="sm"
                    className={`w-36 h-10 ${(() => {
                        const billboard = featuredBillboards && featuredBillboards[currentIndex];
                        if (!billboard) return "bg-gray-400 cursor-not-allowed";

                        let itemPrice = 0;
                        if (filters?.category === 'packages') {
                          itemPrice = billboard?.totalPrice || billboard?.price || 0;
                        } else if (billboard?.type === 'DIGITAL') {
                          itemPrice = priceFormat === 'spot-based'
                            ? billboard?.perSpotPrice
                            : billboard?.perDayPrice;
                        } else {
                          itemPrice = (billboard?.sellingRate?.monthly?.month1 && billboard.sellingRate.monthly.month1 > 0)
                            ? billboard.sellingRate.monthly.month1
                            : billboard?.currentPrice;
                        }
                        const isPricedOut = !itemPrice || itemPrice === 0;
                        const isInactive = billboard?.active === false;

                        if (isPricedOut || isInactive) {
                          return "bg-gray-400 cursor-not-allowed";
                        }
                        return currentImageInCart
                          ? "bg-red-600 hover:bg-red-700 text-white"
                          : "bg-emerald-600 hover:bg-emerald-700 text-white";
                      })()
                      } text-white disabled:bg-gray-400 disabled:cursor-not-allowed`}
                    disabled={(() => {
                      const billboard = featuredBillboards && featuredBillboards[currentIndex];
                      if (!billboard) return true;

                      let itemPrice = 0;
                      if (filters?.category === 'packages') {
                        itemPrice = billboard?.totalPrice || billboard?.price || 0;
                      } else if (billboard?.type === 'DIGITAL') {
                        itemPrice = priceFormat === 'spot-based'
                          ? billboard?.perSpotPrice
                          : billboard?.perDayPrice;
                      } else {
                        itemPrice = (billboard?.sellingRate?.monthly?.month1 && billboard.sellingRate.monthly.month1 > 0)
                          ? billboard.sellingRate.monthly.month1
                          : billboard?.currentPrice;
                      }
                      const isPricedOut = !itemPrice || itemPrice === 0;
                      const isInactive = billboard?.active === false;
                      return isPricedOut || isInactive;
                    })()}
                  >
                    {(() => {
                      const billboard = featuredBillboards && featuredBillboards[currentIndex];
                      if (!billboard) return "Unavailable";

                      let itemPrice = 0;
                      if (filters?.category === 'packages') {
                        itemPrice = billboard?.totalPrice || billboard?.price || 0;
                      } else if (billboard?.type === 'DIGITAL') {
                        itemPrice = priceFormat === 'spot-based'
                          ? billboard?.perSpotPrice
                          : billboard?.perDayPrice;
                      } else {
                        itemPrice = (billboard?.sellingRate?.monthly?.month1 && billboard.sellingRate.monthly.month1 > 0)
                          ? billboard.sellingRate.monthly.month1
                          : billboard?.currentPrice;
                      }
                      const isPricedOut = !itemPrice || itemPrice === 0;
                      const isInactive = billboard?.active === false;

                      if (isPricedOut) return "Unavailable";
                      if (isInactive) return "Sold Out";
                      return currentImageInCart ? "Remove" : "Add to Cart";
                    })()}
                  </Button>
                </div>
              </div>
              <div className="absolute top-4 right-4 flex space-x-2">
                <Button
                  variant="secondary"
                  size="icon"
                  onClick={prevSlide}
                  className="w-10 h-10 bg-emerald-600/80 hover:bg-emerald-700 text-white border-none"
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <Button
                  variant="secondary"
                  size="icon"
                  onClick={nextSlide}
                  className="w-10 h-10 bg-emerald-600/80 hover:bg-emerald-700 text-white border-none"
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>

              {/* Slide indicators */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                {billboardsToDisplay.map((_: any, index: number) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`w-3 h-3 rounded-full transition-colors ${index === currentIndex ? 'bg-white' : 'bg-white/50'
                      }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

